import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import './ChooseLevel.css';

const levelColors = {
    A1: { color: '#E1F5EE', border: '#9FE1CB', text: '#085041' },
    A2: { color: '#E1F5EE', border: '#9FE1CB', text: '#085041' },
    B1: { color: '#E6F1FB', border: '#B5D4F4', text: '#0C447C' },
    B2: { color: '#EEEDFE', border: '#CECBF6', text: '#3C3489' },
    C1: { color: '#FAEEDA', border: '#FAC775', text: '#633806' },
    C2: { color: '#FAEEDA', border: '#FAC775', text: '#633806' },
};

const levelCodes = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

function ChooseLevel() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useLanguage();
    const { updateActiveLevel } = useUserLanguage();
    const isEditMode = !!location.state?.editMode;
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (isEditMode) {
            const current = localStorage.getItem('vozoly_level');
            if (current) setSelected(current);
        }
    }, [isEditMode]);

    const handleConfirm = async () => {
        if (!selected) return;
        localStorage.setItem('vozoly_level', selected);

        if (isEditMode) {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                await supabase
                    .from('profiles')
                    .update({ level: selected })
                    .eq('id', user.id);
                await updateActiveLevel(selected);
            }
            navigate('/profile');
        } else {
            navigate('/signup');
        }
    };

    return (
        <div className="cl-screen">

            <div className="cl-top">
                {isEditMode ? (
                    <button className="cl-back" onClick={() => navigate('/profile')}>✕</button>
                ) : (
                    <button className="cl-back" onClick={() => navigate('/onboarding/path')}>←</button>
                )}
                {!isEditMode && <div className="cl-step">{t('onboarding.path.step')}</div>}
            </div>

            <div className="cl-content">
                <div className="cl-heading">{t('onboarding.chooseLevel.heading')}</div>
                <div className="cl-subheading">
                    {t('onboarding.chooseLevel.subheading')}
                </div>

                <div className="cl-list">
                    {levelCodes.map(code => {
                        const lc = levelColors[code];
                        return (
                            <div
                                key={code}
                                className={`cl-card ${selected === code ? 'selected' : ''}`}
                                style={selected === code ? {
                                    background: lc.color,
                                    borderColor: lc.border,
                                } : {}}
                                onClick={() => setSelected(code)}
                            >
                                <div
                                    className="cl-level-badge"
                                    style={selected === code ? {
                                        background: lc.border,
                                        color: lc.text,
                                    } : {}}
                                >
                                    {code}
                                </div>
                                <div className="cl-level-info">
                                    <div
                                        className="cl-level-name"
                                        style={selected === code ? { color: lc.text } : {}}
                                    >
                                        {t(`onboarding.chooseLevel.levels.${code}.label`)}
                                    </div>
                                    <div
                                        className="cl-level-desc"
                                        style={selected === code ? { color: lc.text, opacity: 0.8 } : {}}
                                    >
                                        {t(`onboarding.chooseLevel.levels.${code}.desc`)}
                                    </div>
                                </div>
                                <div className={`cl-radio ${selected === code ? 'checked' : ''}`}
                                    style={selected === code ? {
                                        borderColor: lc.text,
                                        background: lc.text,
                                    } : {}}
                                >
                                    {selected === code && <div className="cl-radio-dot"></div>}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="cl-note">
                    {t('onboarding.chooseLevel.note')}
                    <span className="cl-test-link" onClick={() => {
                        localStorage.setItem('vozoly_placement_path', 'test');
                        navigate('/onboarding/placement-test', { state: { retake: isEditMode } });
                    }}> {t('onboarding.chooseLevel.takeTest')} →</span>
                </div>

            </div>

            <div className="cl-footer">
                <button
                    className="cl-confirm-btn"
                    disabled={!selected}
                    onClick={handleConfirm}
                >
                    {selected ? (isEditMode ? `${t('onboarding.chooseLevel.saveLevel')}: ${selected} →` : `${t('onboarding.chooseLevel.startAt')} ${selected} →`) : t('onboarding.chooseLevel.chooseLevel')}
                </button>
            </div>

        </div>
    );
}

export default ChooseLevel;